import { createAction, createReducer } from "@reduxjs/toolkit"
import { api } from "./helpers/api"
import { LOADING_INITIAL_STATE, setFailed, setReceived, setRequested } from "modules/loader-watchers/helpers/loading"

const SESSION_KEY = "guest_booking_access"

// Actions
const verifyRequested = createAction("guestAccess/verifyRequested")
const verifySuccess = createAction("guestAccess/verifySuccess")
const verifyFailed = createAction("guestAccess/verifyFailed")
const cleared = createAction("guestAccess/cleared")

const getInitialState = () => {
  try {
    const stored = sessionStorage.getItem(SESSION_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      return {
        ...LOADING_INITIAL_STATE,
        authorized: parsed.authorized || false,
        bookingUuid: parsed.bookingUuid || null,
        error: null
      }
    }
  } catch (e) {
    // ignore malformed session data
  }
  return {
    ...LOADING_INITIAL_STATE,
    authorized: false,
    bookingUuid: null,
    error: null
  }
}

const guestAccessReducer = createReducer(getInitialState(), {
  [verifyRequested.type]: (state) => {
    setRequested(state)
    state.error = null
  },
  [verifySuccess.type]: (state, action) => {
    setReceived(state)
    state.authorized = true
    state.bookingUuid = action.payload.bookingUuid
    sessionStorage.setItem(
      SESSION_KEY,
      JSON.stringify({ authorized: true, bookingUuid: action.payload.bookingUuid })
    )
  },
  [verifyFailed.type]: (state, action) => {
    setFailed(state)
    state.authorized = false
    state.error =
      action.payload?.data?.message ||
      action.payload?.message ||
      "Email does not match. Please try again."
  },
  [cleared.type]: () => {
    sessionStorage.removeItem(SESSION_KEY)
    localStorage.removeItem("guest_token")
    return { ...LOADING_INITIAL_STATE, authorized: false, bookingUuid: null, error: null }
  }
})

export default guestAccessReducer

// Action Creators

/**
 * Verifies that the provided email matches the primary guest for the booking.
 * On success, the backend returns a guest_token which is stored automatically
 * by the api helper, enabling subsequent authenticated API calls.
 */
export const verifyGuestEmail = (uuid, email) => async (dispatch) => {
  dispatch(verifyRequested())
  try {
    const result = await dispatch(
      api({
        url: `/client/trip_bookings/${uuid}/authorize_client`,
        method: "get",
        params: { email },
        noAuth: true,
        suppressErrorNotification: true,
        props: { showToast: false }
      })
    )
    // Store tokens so authHeader() includes them in all subsequent API calls
    if (result?.access_token) {
      localStorage.setItem("guest_token", result.access_token)
    }
    if (result?.refresh_token) {
      localStorage.setItem("refresh_token", result.refresh_token)
    }
    dispatch(verifySuccess({ bookingUuid: uuid }))
    // Dispatch custom event to notify components
    window.dispatchEvent(new Event('guestAuthSuccess'))
    return result
  } catch (error) {
    dispatch(verifyFailed(error))
    throw error
  }
}

export const clearGuestAccess = () => cleared()
